// ABOUTME: Tells a real barge-in apart from the agent's own voice coming back in through the mic.
// ABOUTME: Pure maths, no DOM — the VAD loop in App.js calls it once per frame.
//
// The incident (2026-09-08, watches demo: the welcome "cuts off mid sentence then resumes", and on
// a good run "slightly hiccups")
// ------------------------------------------------------------------------------------------------
// From the guest's own machine, two logs of the same session. The browser console:
//
//     VAD: Interruption Triggered (Rising Edge)
//     VAD: Speech Started (Rising Edge) / Speech Ended (Falling Edge)   x3
//
// and the backend, for that same session, showing ONE welcome turn delivered whole and never
// interrupted. Nobody was talking. The agent's voice was leaving the laptop speakers and arriving
// back at the mic array sitting underneath them, saturating the detector; the rising edge routed
// that to `onInterrupt`, whose first act is an instant flush of the queued audio. The guest heard
// the welcome start, stop dead, and resume wherever the socket had reached — the flushed words
// simply gone. `echoCancellation: true` is requested and is plainly not coping on that hardware.
//
// The rule: while the agent is talking, the room has to beat the AGENT, not merely a fixed
// threshold. Loopback is the agent's own output minus the speakers, the air, and whatever the AEC
// does remove, so it lands below the reference; a guest speaking into their own laptop lands at or
// above it. With the agent silent the reference decays away and the rule vanishes, so ordinary
// listening is untouched.

/**
 * Per-frame release on the reference level. ~300ms at 60fps.
 *
 * A decaying PEAK rather than the instantaneous value, for two reasons. The loopback reaches the
 * mic a few milliseconds late, so a same-frame comparison misaligns at every onset. And the agent's
 * voice drops to nothing between words, which would take the reference to zero exactly in the gaps
 * where its own echo is still ringing and most able to trip the detector.
 */
export const ECHO_DECAY_PER_FRAME = 0.94;

/**
 * How loud the room must be relative to the agent's own output before it counts as a barge-in.
 *
 * Below 1 on purpose. Requiring the room to strictly exceed the agent's digital level is too
 * strict: a guest a normal distance from a laptop mic can sit slightly under it and would lose the
 * ability to interrupt at all, which is a worse failure than the one being fixed. 0.6 clears
 * ordinary speech comfortably while still rejecting loopback, which after even partial echo
 * cancellation arrives well below half the source level.
 *
 * Tunable live on the REACT_APP_VAD_TUNER panel: raise it if the agent still cuts itself off,
 * lower it if a real barge-in stops being heard.
 */
export const ECHO_REJECT_RATIO_DEFAULT = 0.6;

/**
 * Fold this frame's agent level into the decaying reference.
 *
 * @param {number} previous  last frame's reference
 * @param {number} agentVol  this frame's RMS of the agent's own output
 * @param {number} [decay]
 * @returns {number} the new reference — rises instantly, falls slowly
 */
export function decayAgentEcho(previous, agentVol, decay = ECHO_DECAY_PER_FRAME) {
  return Math.max(agentVol || 0, (previous || 0) * decay);
}

/**
 * May this frame's room level count toward interrupting the agent?
 *
 * Always yes when the agent is not speaking: there is no echo to reject, and the caller's absolute
 * threshold is the only gate — which is exactly the pre-existing behaviour.
 *
 * @param {object}  p
 * @param {number}  p.micVol        this frame's RMS at the microphone
 * @param {number}  p.agentEcho     the decaying reference from `decayAgentEcho`
 * @param {boolean} p.agentSpeaking is the agent audibly talking right now
 * @param {number}  [p.ratio]
 */
export function roomBeatsEcho({ micVol, agentEcho, agentSpeaking, ratio = ECHO_REJECT_RATIO_DEFAULT }) {
  if (!agentSpeaking) return true;
  // A ratio of 0 disables the gate entirely, which is what the tuning panel's left stop means.
  if (!ratio) return true;
  return micVol > (agentEcho || 0) * ratio;
}
